import {useMemo} from 'react';
import {gql} from '@apollo/client';
import {camelCase} from 'lodash';
import {useRouter} from 'next/router';

import {
  ActionPlanAdjustmentListFragment,
  HealthFactorAssessmentAnswer,
  HealthFactorAssessmentQuestion,
  QuestionTypeEnum,
  useHealthFactorAssessmentQuery,
  useHealthFactorQuery,
} from '#apollo/schema';
import {ImpactLevel} from '#constant/impactLevel';
import {colorValues} from '#theme/default/colors/colors';

const getHealthFactorAccentColor = (
  impactLevel: ImpactLevel,
  meetsRequirements: boolean,
  emergingResearch: boolean = false,
): keyof typeof colorValues => {
  if (emergingResearch) {
    return 'lemon';
  }
  if (meetsRequirements) {
    switch (camelCase(impactLevel.valueOf())) {
      case ImpactLevel.low:
        return 'pink';
      case ImpactLevel.medium:
        return 'eggplant';
      case ImpactLevel.high:
        return 'eggplantDark';
    }
  } else {
    switch (camelCase(impactLevel.valueOf())) {
      case ImpactLevel.low:
        return 'peach';
      case ImpactLevel.medium:
        return 'tangerine';
      case ImpactLevel.high:
        return 'orange';
    }
  }
  return 'peach';
};

const getAnswerText = (
  question?: HealthFactorAssessmentQuestion,
  answer?: HealthFactorAssessmentAnswer,
) => {
  if (!question || !answer) {
    return '';
  }
  switch (question.questionType) {
    case QuestionTypeEnum.MultipleChoiceEnum:
      const optionArray = answer?.answerOptionIds?.map(answerOptionId => {
        const option = question.multipleChoiceConfiguration?.options.find(
          option => option.id === answerOptionId,
        );
        return option?.text;
      });
      return optionArray?.join(', ');
    case QuestionTypeEnum.NumericEnum:
      return answer.answerNumeric?.toString();
    case QuestionTypeEnum.ShortTextEnum:
      return answer.answerText;
    case QuestionTypeEnum.LongTextEnum:
      return answer.answerText;
  }
};

export const healthFactorQuery = gql`
  query HealthFactorQuery($input: GetHealthFactorInput!) {
    healthFactor(input: $input) {
      id
      imageUrl
      impactLevel
      recommendationDescription
      recommendationTitle
      recommendedResponse
      title
      emergingResearch

      media {
        id
        thumbnailUrl
        url
      }

      references {
        id
        labels
        title
        url
      }

      researchSnack {
        id
        description
        title
      }
    }
  }
`;

export const healthFactorAssessmentQuery = gql`
  ${ActionPlanAdjustmentListFragment}

  query HealthFactorAssessmentQuery($input: GetSurveyAssessmentInput!) {
    me {
      id
      mostRecentLabSubmissionWithResults {
        id
        userSurveySubmission {
          id
          surveyAssessment {
            id
            selectedActionPlan {
              adjustments {
                ...ActionPlanAdjustmentListFragment
              }
              id
            }
          }
        }
      }
    }
    surveyAssessment(input: $input) {
      id

      categories {
        id

        category {
          id

          steps {
            id

            question {
              ...HealthFactorAssessmentQuestion
            }
          }
        }

        factorAssessments {
          id
          answerId
          meetsRequirements

          healthFactor {
            id
            imageUrl
            impactLevel
            questionId
            recommendationDescription
            recommendationTitle
            recommendedResponse
            title
            emergingResearch

            media {
              id
              thumbnailUrl
              url
            }

            references {
              id
              labels
              title
              url
            }

            researchSnack {
              id
              description
              title
            }
          }
        }
      }

      userSurveySubmission {
        id

        answers {
          ...HealthFactorAssessmentAnswer
        }
      }
    }
  }

  fragment HealthFactorAssessmentQuestion on Question {
    id
    text
    questionType
    multipleChoiceConfiguration {
      questionChoiceType
      options {
        id
        text
      }
    }
  }

  fragment HealthFactorAssessmentAnswer on UserSurveySubmissionAnswer {
    answerDate
    answerNumeric
    answerOptionIds
    answerText
    questionId
  }
`;

export const useHealthFactorAssessment = () => {
  const {query} = useRouter();
  const {surveySubmissionId, categoryId, healthFactorId} = query;
  const {data, loading} = useHealthFactorAssessmentQuery({
    variables: {input: {submissionId: (surveySubmissionId as string) || ''}},
  });
  const healthFactorQuery = useHealthFactorQuery({
    variables: {input: {healthfactorId: (healthFactorId as string) || ''}},
  });
  const {selectedActionPlan} =
    data?.me.mostRecentLabSubmissionWithResults?.userSurveySubmission?.surveyAssessment || {};
  const {categories, userSurveySubmission} = data?.surveyAssessment || {};

  const categoryAssessment = useMemo(
    () => categories?.find(category => category.category.id === categoryId),
    [categoryId, categories],
  );
  const healthFactorAssessment = useMemo(
    () =>
      categoryAssessment?.factorAssessments?.find(
        ({healthFactor}) => healthFactor?.id === healthFactorId,
      ),
    [categoryAssessment, healthFactorId],
  );

  const hasActionPlan = !!selectedActionPlan;
  const steps = categoryAssessment?.category?.steps;
  const questionId = healthFactorAssessment?.healthFactor?.questionId;

  const questionStep = useMemo(
    () => steps?.find(step => step.question.id === questionId),
    [steps, questionId],
  );

  const question = questionStep?.question;
  const answers = userSurveySubmission?.answers;

  const answer = useMemo(
    () => answers?.find(answer => answer.questionId === question?.id),
    [question, answers],
  );

  const answerText = useMemo(() => getAnswerText(question, answer), [question, answer]);

  const healthFactor =
    healthFactorAssessment?.healthFactor || healthFactorQuery?.data?.healthFactor;

  const references = healthFactor?.references;
  const researchSnack = healthFactor?.researchSnack;
  const impactLevel = (healthFactor?.impactLevel || ImpactLevel.low) as ImpactLevel;
  const meetsRequirements = healthFactorAssessment?.meetsRequirements ?? true;

  const color = useMemo(
    () =>
      getHealthFactorAccentColor(impactLevel, meetsRequirements, !!healthFactor?.emergingResearch),
    [impactLevel, meetsRequirements, healthFactor],
  );

  return {
    answer: answerText,
    color,
    hasActionPlan,
    healthFactor,
    healthFactorAssessment,
    loading,
    question,
    references,
    researchSnack,
  };
};
